import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from '@reach/router';
import { Offer } from 'src/common/models/offer';
import { useEffect, useMemo, useState } from 'react';
import { customerTypeSelector } from 'src/app/state/customerType/customerTypeSelectors';
import { navigate } from 'gatsby';
import {
  createOptionsList,
  findOfferWithParams,
} from 'src/app/product/services/helpers';
import { productsActions } from 'src/app/product/state/products/productsActions';
import { buildProductItemPageRoute } from 'src/app/routes';
import { Product } from 'src/app/common/models/product';

export const useLeasingCalculatorConfigurations = (
  activeOffer: Offer,
  leasingOffers: Offer[],
  product: Product
) => {
  const location = useLocation();
  const reduxDispatch = useDispatch();
  const userCustomerType = useSelector(customerTypeSelector);

  const hasPresets = !!(
    activeOffer.initialPaymentPresets?.length ||
    activeOffer.contractPeriodPresets?.length ||
    activeOffer.buyoutPricePresets?.length
  );

  const [selectedInitialPayment, setSelectedInitialPayment] = useState(
    () => activeOffer.initialPaymentPresets?.[0] ?? activeOffer.initialPayment
  );
  const [selectedContractPeriod, setSelectedContractPeriod] = useState(
    () => activeOffer.contractPeriodPresets?.[0] ?? activeOffer.contractPeriod
  );
  const [selectedBuyoutPrice, setSelectedBuyoutPrice] = useState(
    () => activeOffer.buyoutPricePresets?.[0] ?? activeOffer.buyoutPrice
  );

  useEffect(() => {
    setSelectedInitialPayment(
      activeOffer.initialPaymentPresets?.[0] ?? activeOffer.initialPayment
    );
    setSelectedContractPeriod(
      activeOffer.contractPeriodPresets?.[0] ?? activeOffer.contractPeriod
    );
    setSelectedBuyoutPrice(
      activeOffer.buyoutPricePresets?.[0] ?? activeOffer.buyoutPrice
    );
  }, [activeOffer.id]);

  const leasingInitialPaymentsList = useMemo(
    () =>
      activeOffer.initialPaymentPresets?.length
        ? activeOffer.initialPaymentPresets
        : createOptionsList('initialPayment', {
            financingType: activeOffer.financingType,
          })(leasingOffers),
    [activeOffer, leasingOffers]
  );

  const leasingContractPeriodsList = useMemo(
    () =>
      activeOffer.contractPeriodPresets?.length
        ? activeOffer.contractPeriodPresets
        : createOptionsList('contractPeriod', {
            financingType: activeOffer.financingType,
          })(leasingOffers),
    [activeOffer, leasingOffers]
  );

  const leasingBuyoutPriceList = useMemo(
    () =>
      activeOffer.buyoutPricePresets?.length
        ? activeOffer.buyoutPricePresets
        : createOptionsList('buyoutPrice', {
            contractPeriod: activeOffer.contractPeriod,
            initialPayment: activeOffer.initialPayment,
          })(leasingOffers),
    [activeOffer, leasingOffers]
  );

  const activeInitialPayment = hasPresets
    ? selectedInitialPayment
    : activeOffer.initialPayment;
  const activeContractPeriod = hasPresets
    ? selectedContractPeriod
    : activeOffer.contractPeriod;
  const activeBuyoutPrice = hasPresets
    ? selectedBuyoutPrice
    : activeOffer.buyoutPrice;

  useEffect(() => {
    if (activeOffer == null) {
      return;
    }

    const newUrl = buildProductItemPageRoute({
      annualMileage: activeOffer.annualMileage,
      brandCode: product.brand?.code,
      buyoutPrice: activeOffer.buyoutPrice,
      contractPeriod: activeOffer.contractPeriod,
      customerType: activeOffer.customerType,
      financingType: activeOffer.financingType,
      initialPayment: activeOffer.initialPayment,
      modelLabel: product.model?.label,
      packageType: activeOffer.packageType,
      productId: product.id,
      version: product.version,
    });

    const params = new URLSearchParams(location.search);

    params.set('offerId', activeOffer.id);
    const details = activeOffer?.external?.details;
    params.set(
      'offerDetails',
      btoa(
        JSON.stringify({
          buyoutPrice: details?.purchase != null ? Number(details.purchase) : activeOffer?.buyoutPrice,
          contractPeriod: details?.month != null ? Number(details.month) : activeOffer?.contractPeriod,
          customerType: activeOffer.customerType,
          financingType: activeOffer?.financingType,
          initialPayment: details?.down != null ? Number(details.down) : activeOffer?.initialPayment,
          price: activeOffer.external?.priceB2C ?? activeOffer?.price,
          priceNetto: activeOffer.external?.priceB2B ?? activeOffer?.price,
        })
      )
    );

    navigate(`${newUrl}?${params}`, {
      replace: true,
    });
  }, [activeOffer, userCustomerType]);

  return {
    leasingInitialPaymentsList,
    activeInitialPayment,
    setLeasingInitialPayment: hasPresets
      ? (value: number) => {
          setSelectedInitialPayment(value);
          reduxDispatch(productsActions.setActiveOffer({ ...activeOffer, initialPayment: value }));
        }
      : (initialPayment: number) =>
          reduxDispatch(
            productsActions.setActiveOffer(
              findOfferWithParams(
                { financingType: activeOffer?.financingType },
                {
                  buyoutPrice: activeOffer?.buyoutPrice,
                  contractPeriod: activeOffer?.contractPeriod,
                  initialPayment,
                }
              )(leasingOffers, activeOffer)
            )
          ),
    leasingContractPeriodsList,
    activeContractPeriod,
    setLeasingContractPeriod: hasPresets
      ? (value: number) => {
          setSelectedContractPeriod(value);
          reduxDispatch(productsActions.setActiveOffer({ ...activeOffer, contractPeriod: value }));
        }
      : (contractPeriod: number) =>
          reduxDispatch(
            productsActions.setActiveOffer(
              findOfferWithParams(
                { financingType: activeOffer?.financingType },
                {
                  buyoutPrice: activeOffer?.buyoutPrice,
                  contractPeriod,
                  initialPayment: activeOffer?.initialPayment,
                }
              )(leasingOffers, activeOffer)
            )
          ),
    leasingBuyoutPriceList,
    activeBuyoutPrice,
    setLeasingBuyoutPrice: hasPresets
      ? (value: number) => {
          setSelectedBuyoutPrice(value);
          reduxDispatch(productsActions.setActiveOffer({ ...activeOffer, buyoutPrice: value }));
        }
      : (buyoutPrice: number) =>
          reduxDispatch(
            productsActions.setActiveOffer(
              findOfferWithParams(
                { financingType: activeOffer?.financingType },
                {
                  buyoutPrice,
                  contractPeriod: activeOffer?.contractPeriod,
                  initialPayment: activeOffer?.initialPayment,
                }
              )(leasingOffers, activeOffer)
            )
          ),
  };
};
